import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface VolcesSeededit30I2iParams {
  /**
   * 编辑提示词，可选，不超过300个中文字符或600个英文单词，默认为空字符串
   * @default ""
   */
  prompt?: string;
  /**
   * 输入图像，必填，支持JPEG或PNG格式的Base64编码或可访问的URL
   */
  image: string;
  /**
   * 输出图像尺寸，格式如'1024x1024'
   */
  size?: string;
  /**
   * 随机种子，范围-1到2147483647
   */
  seed?: number;
  /**
   * 引导强度，范围1-10
   */
  guidance_scale?: number;
  /**
   * 响应格式
   * @values url | b64_json
   */
  response_format?: 'url' | 'b64_json';
  /**
   * 是否添加水印
   */
  watermark?: boolean;
  /**
   * 是否启用流式输出
   */
  stream?: boolean;
}

export interface VolcesSeededit30I2iResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * volces_seededit_3_0_i2i
 *
 * Volces SeedEdit 3.0 I2I Image-to-Image Editing Generator
 * 
 * Model: doubao-seededit-3-0-i2i-250628
 * Features: 图像到图像编辑，支持基于提示词的图像内容修改
 * Format Support: JPEG, PNG (Base64编码或URL)
 * Size Limit: 最大10MB, 最大6000x6000像素, 最小14px
 * Aspect Ratio: 1:3 到 3:1
 * 
 * API Documentation: https://www.volcengine.com/docs/82379/1541523
 *
 * @example
 * ```typescript
 * import { initSeacloud, volcesSeededit30I2i } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await volcesSeededit30I2i({
 *   // 参数见 VolcesSeededit30I2iParams 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 VolcesSeededit30I2iParams 接口定义
 * @returns 生成的资源列表
 */
export async function volcesSeededit30I2i(
  params: VolcesSeededit30I2iParams,
): Promise<VolcesSeededit30I2iResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'volces_seededit_3_0_i2i',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: VolcesSeededit30I2iResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
